import { Controller } from "@hotwired/stimulus";

const EXCLUSIVE_REPLY_OPTIONS = new Set([
  "everyone_can_reply",
  "no_reply"
]);
const DEFAULT_REPLY_OPTION = "everyone_can_reply";

export default class DropdownController extends Controller {

  connect() {
    this.updateReplyOptionDisplay();

    if (this.element.classList.contains("comment-dropdown")) {
      const toggle = this.element.querySelector(':scope > [data-bs-toggle="dropdown"]');
      if (toggle) {
        this.commentToggle = toggle;
        this.boundCloseOnScroll = (event) => {
          const menu = this.element.querySelector(".own-comment-menu");
          if (menu?.contains(event.target)) return;
          globalThis.bootstrap?.Dropdown.getInstance(this.commentToggle)?.hide();
        };
        this.boundWatchScroll = () => {
          document.addEventListener("scroll", this.boundCloseOnScroll, true);
        };
        this.boundStopWatchingScroll = () => this.stopWatchingScroll();
        this.element.addEventListener("shown.bs.dropdown", this.boundWatchScroll);
        this.element.addEventListener("hidden.bs.dropdown", this.boundStopWatchingScroll);
        globalThis.bootstrap?.Dropdown.getOrCreateInstance(toggle, {
          popperConfig: (config) => ({
            ...config,
            // A viewport-positioned menu does not widen the scrollable post.
            strategy: "fixed",
            modifiers: [
              ...config.modifiers,
              {
                name: "eventListeners",
                options: { scroll: false, resize: true }
              },
              {
                name: "preventOverflow",
                options: { rootBoundary: "viewport", padding: 8, altAxis: true, tether: false }
              }
            ]
          })
        });
      }
    }

    this.managesExclusiveState = this.element.matches(
      "#updated_comment_list .dropdown, .group-posts .dropdown"
    );

    if (this.managesExclusiveState) {
      this.boundHandleShow = this.handleShow.bind(this);
      this.element.addEventListener("show.bs.dropdown", this.boundHandleShow);
    }
  }

  disconnect() {
    if (this.boundWatchScroll) {
      this.element.removeEventListener("shown.bs.dropdown", this.boundWatchScroll);
      this.element.removeEventListener("hidden.bs.dropdown", this.boundStopWatchingScroll);
      this.stopWatchingScroll();
    }
    if (this.boundHandleShow) {
      this.element.removeEventListener("show.bs.dropdown", this.boundHandleShow);
    }
  }

  handleShow(event) {
    const targetDropdown = event.target.closest(".dropdown");
    if (!targetDropdown) return;

    // Manage single-open dropdown state across comments and posts sections
    for (const dropdown of document.querySelectorAll("#updated_comment_list .dropdown, .group-posts .dropdown")) {
      if (dropdown === targetDropdown) continue;

      const dropdownToggle = dropdown.querySelector(":scope > [data-bs-toggle=\"dropdown\"]");
      const dropdownMenu = dropdown.querySelector(".dropdown-menu");
      if (dropdownToggle && dropdownMenu?.classList.contains("show")) {
        globalThis.bootstrap?.Dropdown.getOrCreateInstance(dropdownToggle).hide();
      }
    }
  }

  stopWatchingScroll() {
    document.removeEventListener("scroll", this.boundCloseOnScroll, true);
  }

  close(event) {
    event.preventDefault();
    event.stopPropagation();

    this.dismiss(event);
  }

  dismiss(event) {
    const dropdown = event.currentTarget.closest(".dropdown");
    const toggle = dropdown?.querySelector(":scope > [data-bs-toggle=\"dropdown\"]");
    if (!toggle) return;

    globalThis.bootstrap?.Dropdown
      .getOrCreateInstance(toggle)
      .hide();
  }

  reply_option(event) {
    const target = event.currentTarget;
    const nestedForm = this.element.closest("[data-controller=\"nested-form\"]");

    if (nestedForm) {
      for (const group of nestedForm.querySelectorAll(".reply-opt-select")) {
        const options = Array.from(group.querySelectorAll(".form-check-input"));

        const currentOption = options.find(
          option => option.value === target.value
        );

        if (!currentOption) continue;

        // mirror checked/unchecked state
        currentOption.checked = target.checked;

        this.applyReplyOptionRules(currentOption, options);
        this.updateReplyOptionDisplay(options, group.closest("[data-controller=\"dropdown\"]"));
      }

      return;
    }

    this.applyReplyOptionRules(target, this.replyOptions);
    this.updateReplyOptionDisplay();
  }

  get replyOptions() {
    return Array.from(
      this.element.querySelectorAll(
        ".reply-opt-select .form-check-input"
      )
    );
  }

  updateReplyOptionDisplay(options = this.replyOptions, container = this.element) {
    const selectedLabels = options
      .filter(option => option.checked)
      .map(option => option.nextElementSibling.textContent.trim());

    const display = container.querySelector(".selected-value-display");

    if (display) {
      display.textContent =
        selectedLabels.join(", ") || "Select Reply Options";
    }
  }

  applyReplyOptionRules(target, options) {
    if (target.checked && EXCLUSIVE_REPLY_OPTIONS.has(target.value)) {
      for (const option of options) {
        option.checked = option === target;
      }
    } else if (target.checked) {
      for (const option of options) {
        if (EXCLUSIVE_REPLY_OPTIONS.has(option.value)) {
          option.checked = false;
        }
      }
    }

    if (!options.some(option => option.checked)) {
      const defaultOption = options.find(
        option => option.value === DEFAULT_REPLY_OPTION
      );

      if (defaultOption) defaultOption.checked = true;
    }
  }
}
